import { finalizeOrderLoyalty } from "./loyalty";
import { getSupabaseClient } from "./index";
import {
  ORDERS_PAGE_SIZE,
  computeOrderTotals,
  deskStepToStatuses,
  mapOrderLine,
  mapOrderRow,
  nextStatusAfterAction,
  statusToPipelineStage,
  validateCreateStaffOrder,
  type CreateStaffOrderInput,
  type OrderDbStatus,
  type OrderDeskFilter,
  type OrderDeskStats,
  type OrderDetailRecord,
  type OrderFulfillment,
  type OrderRecord,
} from "@veorest/utils";

export type {
  OrderRecord,
  OrderDetailRecord,
  OrderDeskStats,
  OrderDeskFilter,
  CreateStaffOrderInput,
};

export interface ListOrdersParams {
  tenantId: string;
  filter?: OrderDeskFilter;
  search?: string;
  limit?: number;
  offset?: number;
  /** Inclusive lower bound on `created_at` (ISO). */
  from?: string;
  /** Inclusive upper bound on `created_at` (ISO). */
  to?: string;
}

export interface ListOrdersResult {
  orders: OrderRecord[];
  hasMore: boolean;
}

async function loadItemCounts(orderIds: string[]): Promise<Map<string, number>> {
  const counts = new Map<string, number>();
  if (!orderIds.length) return counts;
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from("order_items")
    .select("order_id, quantity")
    .in("order_id", orderIds);
  if (error) throw error;
  for (const row of data ?? []) {
    const id = row.order_id as string;
    counts.set(id, (counts.get(id) ?? 0) + Number(row.quantity ?? 1));
  }
  return counts;
}

export async function listOrders({
  tenantId,
  filter = "pending",
  search = "",
  limit = ORDERS_PAGE_SIZE,
  offset = 0,
  from,
  to,
}: ListOrdersParams): Promise<ListOrdersResult> {
  const supabase = getSupabaseClient();
  let query = supabase
    .from("orders")
    .select("*")
    .eq("tenant_id", tenantId)
    .order("created_at", { ascending: false })
    .range(offset, offset + limit);

  const statuses = deskStepToStatuses(filter);
  if (statuses) {
    query = query.in("status", statuses);
  }

  if (from) query = query.gte("created_at", from);
  if (to) query = query.lte("created_at", to);

  const term = search.trim();
  if (term) {
    const pattern = `%${term}%`;
    query = query.or(
      `customer_name.ilike.${pattern},customer_phone.ilike.${pattern},customer_address.ilike.${pattern}`
    );
  }

  const { data, error } = await query;
  if (error) throw error;

  const rows = data ?? [];
  const hasMore = rows.length > limit;
  const page = hasMore ? rows.slice(0, limit) : rows;
  const counts = await loadItemCounts(page.map((r) => r.id as string));

  return {
    orders: page.map((row) =>
      mapOrderRow(row as Record<string, unknown>, counts.get(row.id as string) ?? 0)
    ),
    hasMore,
  };
}

export async function getOrderById(
  tenantId: string,
  orderId: string
): Promise<OrderDetailRecord | null> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from("orders")
    .select("*")
    .eq("tenant_id", tenantId)
    .eq("id", orderId)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;

  const { data: items, error: itemsError } = await supabase
    .from("order_items")
    .select("*")
    .eq("order_id", orderId)
    .order("id");
  if (itemsError) throw itemsError;

  const lines = (items ?? []).map((row) => mapOrderLine(row as Record<string, unknown>));
  const itemCount = lines.reduce((sum, line) => sum + line.quantity, 0);
  const base = mapOrderRow(data as Record<string, unknown>, itemCount);

  return { ...base, items: lines };
}

function mapDeskStatsRow(row: Record<string, unknown>): OrderDeskStats {
  return {
    pending: Number(row.pending ?? row.needs_you ?? 0),
    kitchen: Number(row.kitchen ?? row.cooking ?? 0),
    ready: Number(row.ready ?? 0),
    driver: Number(row.driver ?? row.on_the_way ?? 0),
    done_today: Number(row.done_today ?? 0),
  };
}

export async function getOrderDeskStats(tenantId: string): Promise<OrderDeskStats> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("get_order_desk_stats", {
    p_tenant_id: tenantId,
  });

  if (!error && data) {
    const row = Array.isArray(data) ? data[0] : data;
    if (row) return mapDeskStatsRow(row as Record<string, unknown>);
  }

  // Fallback if RPC not migrated yet
  const { data: orders, error: listError } = await supabase
    .from("orders")
    .select("status, created_at")
    .eq("tenant_id", tenantId);
  if (listError) throw listError;

  const startOfDay = new Date();
  startOfDay.setHours(0, 0, 0, 0);
  const stats: OrderDeskStats = {
    pending: 0,
    kitchen: 0,
    ready: 0,
    driver: 0,
    done_today: 0,
  };

  for (const row of orders ?? []) {
    const status = String(row.status);
    if (status === "draft" || status === "pending_cashier_approval") stats.pending += 1;
    else if (status === "approved" || status === "in_kitchen") stats.kitchen += 1;
    else if (status === "ready_for_dispatch") stats.ready += 1;
    else if (["blasted_to_drivers", "assigned", "picked_up"].includes(status)) {
      stats.driver += 1;
    } else if (
      (status === "delivered" || status === "completed") &&
      new Date(String(row.created_at)).getTime() >= startOfDay.getTime()
    ) {
      stats.done_today += 1;
    }
  }

  return stats;
}

function parseOrderRpcPayload(
  data: unknown,
  itemCount: number
): OrderRecord {
  const payload = data as { order?: Record<string, unknown> } | Record<string, unknown>;
  const row =
    payload && typeof payload === "object" && "order" in payload && payload.order
      ? (payload.order as Record<string, unknown>)
      : (payload as Record<string, unknown>);
  if (!row || typeof row !== "object" || !("id" in row)) {
    throw new Error("Order RPC returned invalid payload");
  }
  return mapOrderRow(row, itemCount);
}

export async function acceptOrder(
  tenantId: string,
  orderId: string,
  prepMinutes: number
): Promise<OrderRecord> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("accept_staff_order", {
    p_tenant_id: tenantId,
    p_order_id: orderId,
    p_prep_minutes: prepMinutes,
  });

  if (error) throw error;
  const counts = await loadItemCounts([orderId]);
  const mapped = parseOrderRpcPayload(data, counts.get(orderId) ?? 0);
  // Idempotent accept: the RPC returns the order as-is when it is already
  // accepted / past accept (double-taps, retries, stale UIs all succeed).
  if (["draft", "pending_cashier_approval", "rejected", "cancelled"].includes(mapped.status)) {
    throw new Error(`Accept did not move order (status=${mapped.status})`);
  }
  return mapped;
}

export async function rejectOrder(
  tenantId: string,
  orderId: string,
  reason: string
): Promise<OrderRecord> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("reject_staff_order", {
    p_tenant_id: tenantId,
    p_order_id: orderId,
    p_reason: reason.trim() || "Declined",
  });

  if (error) throw error;
  const counts = await loadItemCounts([orderId]);
  const mapped = parseOrderRpcPayload(data, counts.get(orderId) ?? 0);
  if (mapped.status !== "rejected") {
    throw new Error(`Reject did not move order (status=${mapped.status})`);
  }
  return mapped;
}

export async function advanceOrder(
  tenantId: string,
  orderId: string,
  action: "mark_ready" | "mark_done" | "start_cooking",
  fulfillment: OrderFulfillment
): Promise<OrderRecord> {
  if (action === ("send_out" as string)) {
    throw new Error("send_out is disabled; assign or request a driver from Ready");
  }
  const status = nextStatusAfterAction(action, fulfillment);
  const supabase = getSupabaseClient();

  const { data, error } = await supabase.rpc("advance_staff_order", {
    p_tenant_id: tenantId,
    p_order_id: orderId,
    p_action: action,
    p_fulfillment: fulfillment || "delivery",
  });

  if (error) {
    const msg = String(error.message || error.details || "Advance failed");
    throw new Error(msg);
  }
  const mapped = parseOrderRpcPayload(
    data,
    (await loadItemCounts([orderId])).get(orderId) ?? 0
  );
  if (mapped.status !== status) {
    throw new Error(
      `Advance did not move order (wanted ${status}, got ${mapped.status})`
    );
  }
  if (action === "mark_done" || status === "completed" || status === "delivered") {
    try {
      await finalizeOrderLoyalty(tenantId, orderId);
    } catch {
      // Loyalty is best-effort; order state is already committed
    }
  }
  return mapped;
}

export async function createStaffOrder(
  tenantId: string,
  input: CreateStaffOrderInput
): Promise<OrderDetailRecord> {
  const errorKey = validateCreateStaffOrder(input);
  if (errorKey) {
    const err = new Error(errorKey);
    err.name = "ValidationError";
    throw err;
  }

  const fee =
    input.fulfillment_type === "delivery" ? Number(input.delivery_fee ?? 0) : 0;
  const totals = computeOrderTotals(input.items, fee);
  // POS staff orders (autoAccept) skip pending → go directly to kitchen
  // Customer orders still go through pending for cashier review
  const status: OrderDbStatus = input.autoAccept
    ? "approved"
    : input.submitForApproval === true
      ? "pending_cashier_approval"
      : "draft";
  const payment_method =
    input.fulfillment_type === "pickup" && input.payment_method === "cod"
      ? "pay_at_pickup"
      : input.payment_method;
  const payment_status =
    payment_method === "cod"
      ? "cod_pending"
      : payment_method === "pay_at_pickup"
        ? "pay_at_pickup_pending"
        : "unpaid";

  const payload = {
    status,
    channel: input.channel ?? "phone",
    pipeline_stage: statusToPipelineStage(status),
    customer_id: input.customer_id ?? null,
    customer_name: input.customer_name.trim(),
    customer_phone: input.customer_phone?.trim() || null,
    customer_address:
      input.fulfillment_type === "delivery"
        ? input.customer_address?.trim() || null
        : null,
    delivery_notes: input.delivery_notes?.trim() || null,
    staff_note: input.staff_note?.trim() || null,
    fulfillment_type: input.fulfillment_type,
    payment_method,
    payment_status,
    subtotal: totals.subtotal,
    delivery_fee: totals.delivery_fee,
    total: totals.total,
    currency: input.currency || "IQD",
    items: input.items.map((item) => ({
      menu_item_id: item.menu_item_id,
      item_name: item.item_name,
      quantity: item.quantity,
      unit_price: item.unit_price,
      special_instructions: item.special_instructions?.trim() || null,
    })),
  };

  const supabase = getSupabaseClient();

  // Prefer SECURITY DEFINER RPC (avoids RLS 403 + works before/after 011 columns)
  const { data: rpcData, error: rpcError } = await supabase.rpc("create_staff_order", {
    p_tenant_id: tenantId,
    p_payload: payload,
  });

  if (!rpcError && rpcData) {
    const result = rpcData as { order: Record<string, unknown>; items: Record<string, unknown>[] };
    const lines = (result.items ?? []).map((row) => mapOrderLine(row));
    const base = mapOrderRow(
      result.order,
      lines.reduce((sum, line) => sum + line.quantity, 0)
    );
    return { ...base, items: lines };
  }

  // Fallback: direct insert with full payload columns (for when RPC not deployed)
  // Includes all columns the RPC would insert — works with pre-011 and post-011 schemas.
  const { data: order, error } = await supabase
    .from("orders")
    .insert({
      tenant_id: tenantId,
      channel: payload.channel,
      status,
      pipeline_stage: payload.pipeline_stage,
      customer_id: payload.customer_id,
      customer_name: payload.customer_name,
      customer_phone: payload.customer_phone,
      customer_address: payload.customer_address,
      delivery_notes: payload.delivery_notes,
      staff_note: payload.staff_note,
      fulfillment_type: payload.fulfillment_type,
      payment_method: payload.payment_method,
      payment_status: payload.payment_status,
      subtotal: totals.subtotal,
      tax: 0,
      delivery_fee: totals.delivery_fee,
      total: totals.total,
      currency: payload.currency,
    })
    .select("*")
    .single();

  if (error) {
    const rpcHint = rpcError?.message ? ` RPC: ${rpcError.message}` : "";
    const err = new Error(`${error.message}${rpcHint}`);
    err.name = error.name || "PostgrestError";
    throw err;
  }

  const orderId = order.id as string;
  const linesPayload = input.items.map((item) => ({
    order_id: orderId,
    menu_item_id: item.menu_item_id,
    quantity: item.quantity,
    unit_price: item.unit_price,
    special_instructions: item.special_instructions?.trim() || null,
  }));

  const { data: insertedItems, error: itemsError } = await supabase
    .from("order_items")
    .insert(linesPayload)
    .select("*");
  if (itemsError) throw itemsError;

  const mappedLines = (insertedItems ?? []).map((row) =>
    mapOrderLine(row as Record<string, unknown>)
  );
  const base = mapOrderRow(
    order as Record<string, unknown>,
    mappedLines.reduce((s, l) => s + l.quantity, 0)
  );
  return { ...base, items: mappedLines };
}
